import type { ChatAttachment, ChatMessage } from '../types'

/**
 * [PROD-93] Additive reconcile for SWR refetches AFTER the initial seed.
 *
 * Refetches defer to the socket as source of truth for live assistant
 * state, BUT persisted user-role rows we don't hold locally must be
 * folded in — e.g. when the first seed raced an empty snapshot, or a
 * user message with attachments was persisted before the seed landed.
 * Assistant rows are never touched: those stream via the socket and
 * re-seeding would clobber in-flight state. Port of the host app's
 * merge in apps/agent/client/src/hooks/use-conversation.ts.
 *
 * Returns the merged, server-ordered list — or null when there is
 * nothing to fold in (caller skips the state update).
 */
export function mergeMissingUserMessages(
  current: ChatMessage[],
  serverMessages: Array<{ id: string; role: string; content: string; attachments?: ChatAttachment[] | null }>,
): ChatMessage[] | null {
  const haveIds = new Set(current.map((m) => m.id))
  const missingUser = serverMessages.filter((m) => m.role.toLowerCase() === 'user' && !haveIds.has(m.id))
  if (missingUser.length === 0) {
    return null
  }
  const merged: ChatMessage[] = [
    ...missingUser.map((m) => ({
      id: m.id,
      role: 'user' as const,
      content: m.content,
      attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
      status: 'complete' as const,
    })),
    ...current,
  ]
  // Server returns messages in createdAt asc order; preserve that for
  // the rows we just folded in.
  const orderIndex = new Map(serverMessages.map((m, i) => [m.id, i]))
  return merged.sort((a, b) => (orderIndex.get(a.id) ?? 1e9) - (orderIndex.get(b.id) ?? 1e9))
}
